import { useCallback, useState } from "react";
import { useNavigate, useOutletContext } from "react-router";
import styled from "styled-components";

import { useDialog } from "~/src/modules/ui-kit";

import Colors from "./Colors";
import SpecificationConfiguration from "./SpecificationConfiguration";
import useApplicationWriter from "./useApplicationWriter";
import useEditor from "./useEditor";
import * as UI from "../../../../modules/ui-kit";
import {
    useUnsavedChangesEditor,
    type UnsavedChangesContext,
} from "../../../../modules/unsaved-changes";
import Application from "../../domain/Application";


const ErrorMessage = styled.p`
    color: #c00;
    font-size: 0.9rem;
    margin: 0;
`;

export default function ApplicationEditor(props) {
    const { application, domain, onSave } = props;

    const guard = useOutletContext<UnsavedChangesContext>();

    const { reader, reset, writer: rawWriter } = useEditor<Application>(application);
    const [save, remove] = useApplicationWriter(reader, onSave);

    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(undefined);
    const [error, setError] = useState<string | null>(null);

    const writer = useCallback(
        (key: string) => (value: unknown) => {
            if (guard && JSON.stringify(reader?.[key]) !== JSON.stringify(value)) {
                guard.isDirtyRef.current = true;
            }
            rawWriter(key)(value);
        },
        [rawWriter, guard, reader],
    );

    const commit = useCallback(
        async (): Promise<boolean> => {
            setSaving(true);
            setError(null);
            try {
                await save();
                setSaved(true);
                if (guard) guard.isDirtyRef.current = false;
                setTimeout(() => setSaved(undefined), 1600);
                return true;
            } catch (e) {
                setSaved(false);
                setError((e as Error)?.message ?? "Failed to save application.");
                setTimeout(() => setSaved(undefined), 1600);
                return false;
            } finally {
                setSaving(false);
            }
        },
        [save, guard],
    );

    const onSubmit = useCallback(
        (event) => {
            event.preventDefault();
            return commit();
        },
        [commit],
    );

    const onReset = useCallback(() => {
        reset();
        if (guard) guard.isDirtyRef.current = false;
    }, [reset, guard]);

    useUnsavedChangesEditor(guard, commit);

    const [Dialog, open, close] = useDialog();

    const navigate = useNavigate();

    async function onDelete() {
        close();
        await remove();
        navigate("..");
    }

    return (
        <UI.Form
            onReset={ onReset }
            onSubmit={ onSubmit }
        >
            {
                domain && (
                    <UI.Field>
                        <a
                            href={ `https://${ reader.domain }.app.bimproject.cloud` }
                            target="_blank" rel="noreferrer"
                        >Open application</a>
                    </UI.Field>
                )
            }
            <UI.Field>
                <UI.FieldLabel>Domain</UI.FieldLabel>
                {
                    domain
                        ? <span>{ reader.domain }</span>
                        : <UI.TextInput
                            onChange={ (value) => writer("domain")(value.toLowerCase()) }
                            placeholder="Application domain"
                            value={ reader.domain ?? "" }
                        />
                }
            </UI.Field>
            <UI.Field>
                <UI.FieldLabel>Name</UI.FieldLabel>
                <UI.TextInput
                    onChange={ writer("name") }
                    placeholder="Application's name"
                    value={ reader.name ?? "" }
                />
            </UI.Field>
            <UI.Field>
                <UI.FieldLabel>About</UI.FieldLabel>
                <UI.TranslatedInput
                    label="about"
                    onChange={ writer("about") }
                    value={ reader.about ?? [] }
                />
            </UI.Field>
            <UI.Field>
                <UI.FieldLabel>Country</UI.FieldLabel>
                <UI.CountrySelect
                    onChange={ writer("country") }
                    value={ reader.country }
                />
            </UI.Field>
            <UI.Field>
                <UI.FieldLabel>Brands</UI.FieldLabel>
                <UI.ListInput
                    onChange={ writer("brandIDs") }
                    value={ reader?.brandIDs ?? [] }
                />
            </UI.Field>
            <UI.Field>
                <UI.FieldLabel>Colors</UI.FieldLabel>
                <Colors
                    onChange={ writer("colors") }
                    value={ reader?.colors }
                />
            </UI.Field>
            <UI.Field>
                <UI.FieldLabel>Favicon</UI.FieldLabel>
                <UI.TextInput
                    onChange={ writer("favicon") }
                    placeholder="Application favicon"
                    value={ reader.favicon ?? "" }
                />
            </UI.Field>
            <UI.Field>
                <UI.FieldLabel>Logo</UI.FieldLabel>
                <UI.TextInput
                    onChange={ writer("logo") }
                    placeholder="Application logo"
                    value={ reader.logo ?? "" }
                />
            </UI.Field>
            <UI.Field>
                <UI.FieldLabel>Cover image</UI.FieldLabel>
                <UI.TextInput
                    onChange={ writer("coverImage") }
                    placeholder="Application cover image"
                    value={ reader.coverImage ?? "" }
                />
            </UI.Field>
            <UI.Field>
                <UI.FieldLabel>Instance specification</UI.FieldLabel>
                <SpecificationConfiguration
                    onChange={ writer("instanceSpecification") }
                    value={ reader.instanceSpecification }
                />
            </UI.Field>
            <UI.Field>
                <UI.FieldLabel>Type specification</UI.FieldLabel>
                <SpecificationConfiguration
                    onChange={ writer("typeSpecification") }
                    value={ reader.typeSpecification }
                />
            </UI.Field>
            <UI.Field>
                <UI.FieldLabel>Features</UI.FieldLabel>
                <UI.ListInput
                    onChange={ writer("features") }
                    value={ reader.features ?? [] }
                />
            </UI.Field>
            { error && <ErrorMessage>{ error }</ErrorMessage> }
            <UI.ButtonGroup>
                <UI.Button
                    $appearance="primary"
                    disabled={ !save || saving || saved }
                    type="submit"
                >
                    { saved === true && "Saved" }
                    { saved === false && "Error" }
                    { typeof saved !== "boolean" && "Save" }
                </UI.Button>
                <UI.Button
                    disabled={ saving }
                    type="reset"
                >
                    Reset
                </UI.Button>
                {
                    domain && (
                        <UI.Button
                            disabled={ saving }
                            onClick={ open }
                        >
                            Delete
                        </UI.Button>
                    )
                }
                <Dialog>
                    <div>
                        <p>Do you really want to delete this application?</p>
                        <UI.ButtonGroup>
                            <UI.Button
                                $appearance="primary"
                                onClick={ onDelete }
                            >
                                Delete
                            </UI.Button>
                            <UI.Button
                                onClick={ close }
                            >
                                Cancel
                            </UI.Button>
                        </UI.ButtonGroup>
                    </div>
                </Dialog>
            </UI.ButtonGroup>
        </UI.Form>
    );
}
